import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconDots } from '@tabler/icons-react';
import NavigationItem from './NavigationItem';

const NavigationUtilityMenu = ({ items, activeMenu, isCollapsed, onMenuClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = event => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = event => {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      buttonRef.current?.focus();
    };

    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  if (items.length === 0) return null;

  const hasActiveItem = items.some(item => item.id === activeMenu);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-expanded={isOpen}
        aria-controls="navigation-utility-menu"
        className={`flex min-h-[40px] w-full items-center ${isCollapsed ? 'justify-center px-2' : 'gap-3 px-3'} text-[13px] font-medium focus:outline-none focus:ring-2 focus:ring-accent ${
          hasActiveItem || isOpen
            ? 'bg-accent-muted text-accent'
            : 'text-text-tertiary hover:bg-surface-3 hover:text-text-primary'
        }`}
        aria-label="More tools"
      >
        <IconDots className="h-[18px] w-[18px] flex-shrink-0" aria-hidden="true" />
        {!isCollapsed && <span>More</span>}
      </button>

      {isOpen && (
        <div
          id="navigation-utility-menu"
          data-origin="bottom-left"
          className={`ui-motion-menu absolute z-50 w-56 border border-glass-border bg-surface-1 p-2 shadow-xl ${
            isCollapsed ? 'bottom-0 left-full ml-2' : 'bottom-full left-0 mb-2'
          }`}
        >
          <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
            Utilities
          </p>
          {items.map(item => (
            <NavigationItem
              key={item.id}
              item={item}
              isActive={activeMenu === item.id}
              onClick={() => {
                setIsOpen(false);
                onMenuClick(item.id, item.path);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

NavigationUtilityMenu.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeMenu: PropTypes.string,
  isCollapsed: PropTypes.bool.isRequired,
  onMenuClick: PropTypes.func.isRequired
};

export default React.memo(NavigationUtilityMenu);
